'use client';

import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { isPortalBuild } from '../lib/appTarget';
import { type CreateDaemonTriggerInput, type ContinueDaemonTriggerInput } from '../lib/api';
import type { RunnerRequestControllerAdapter } from '../lib/runnerRequest';
import { resolveApiErrorToastMessageOrFallback } from '../lib/api/localizeApiError';
import { useContinueDaemonTriggerMutation, useCreateDaemonTriggerMutation } from './queries/useDaemonTriggers';
import { webRunnerRequestSelection as selection } from '../lib/webRunnerRequestSelection';
import type { OpenDaemonTriggerModalDetail } from './useDaemonTriggerModalController';

const subscribeOpen = (listener: (detail?: OpenDaemonTriggerModalDetail) => void) => {
  const handler = (event: Event) => listener((event as CustomEvent<OpenDaemonTriggerModalDetail>).detail);
  window.addEventListener('openDaemonTriggerModal', handler);
  return () => window.removeEventListener('openDaemonTriggerModal', handler);
};

export type WebRunnerRequestControllerAdapter = RunnerRequestControllerAdapter<
  CreateDaemonTriggerInput,
  ContinueDaemonTriggerInput,
  OpenDaemonTriggerModalDetail
>;

export const useWebRunnerRequestControllerAdapter = (
  token: string | null,
  sourceTriggerId: string | null,
  t: (key: string) => string,
  scope?: { teamId: string; projectId: string },
): WebRunnerRequestControllerAdapter => {
  const router = useRouter();
  const createMutation = useCreateDaemonTriggerMutation(token);
  const continueMutation = useContinueDaemonTriggerMutation(token, sourceTriggerId);
  const teamId = scope?.teamId;
  const projectId = scope?.projectId;
  return useMemo(
    () => ({
      createMutation,
      continueMutation,
      selection,
      subscribeOpen,
      feedback: {
        success: toast.success,
        error: toast.error,
        warning: toast.warning,
        resolveError: (error, fallback) => resolveApiErrorToastMessageOrFallback(t, error, fallback),
      },
      // Portal에는 러너 관리 화면이 없어 요청 뒤 결과를 볼 경로가 끊긴다.
      // Portal 빌드에서만 생성·이어쓰기 성공 뒤 본인 실행 상세로 이동한다(web 동작 유지).
      ...(isPortalBuild && teamId !== undefined && projectId !== undefined
        ? {
            navigation: {
              openRunResult: ({
                triggerId,
                teamId: resolvedTeamId,
                projectId: resolvedProjectId,
              }: {
                triggerId: string;
                teamId: string;
                projectId: string;
              }) => {
                const params = new URLSearchParams({
                  id: triggerId,
                  teamId: resolvedTeamId,
                  projectId: resolvedProjectId,
                });
                router.push(`/runs/detail?${params.toString()}`);
              },
            },
          }
        : {}),
    }),
    [createMutation, continueMutation, t, router, teamId, projectId],
  );
};
